'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Oliva de firma del tema (kicker, chip de delivery). */
const OLIVE = '#66682f'

/**
 * Hero del tema "oliva" (rústico mediterráneo): trattoria de yeso y terracota.
 * Composición partida con la foto en ARCO (border-radius superior tipo capilla)
 * a la izquierda en desktop, y el nombre en Newsreader ITÁLICA — la voz del
 * tema. Kicker con doble hairline y aceituna. La barra superior usa modo
 * sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroOliva({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* pt despeja la barra superior fija (navSolid) */}
      <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-24 sm:pt-28 pb-14 sm:pb-16">
        <div className={`grid items-center gap-10 sm:gap-14 ${hasMedia ? 'md:grid-cols-[0.85fr_1.15fr]' : ''}`}>
          {/* Columna foto: arco de capilla. En mobile va después del texto. */}
          {hasMedia && (
            <div
              className="relative w-full overflow-hidden min-h-[320px] order-2 md:order-1"
              style={{
                aspectRatio: '4 / 5',
                borderRadius: '999px 999px 14px 14px',
                border: '1px solid var(--ms-border)',
                boxShadow: 'var(--ms-card-shadow)',
              }}
            >
              {heroVideoUrl ? (
                <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
              ) : heroImageUrl ? (
                <Image src={heroImageUrl} alt={businessName} fill priority sizes="(max-width: 768px) 100vw, 42vw" className="object-cover" />
              ) : null}
            </div>
          )}

          {/* Columna texto */}
          <div className={`min-w-0 ${hasMedia ? 'order-1 md:order-2' : ''}`}>
            <div className="flex items-center gap-3 mb-5">
              <span className="block h-px w-8" style={{ background: OLIVE }} aria-hidden />
              <span className="block w-[7px] h-[10px] rounded-full" style={{ background: OLIVE }} aria-hidden />
              {city && (
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.24em]"
                  style={{ color: 'var(--ms-text-muted)' }}
                >
                  {city}
                </span>
              )}
            </div>

            <h1
              className="italic leading-[1.04] tracking-[-0.01em] text-[clamp(40px,7vw,72px)]"
              style={{ fontFamily: 'var(--ms-font-display)', fontWeight: 500, color: 'var(--ms-text)' }}
            >
              {businessName}
            </h1>

            {tagline && (
              <p className="mt-4 max-w-lg text-[16px] sm:text-[17.5px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
                {tagline}
              </p>
            )}

            {/* Meta discreta */}
            <div
              className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]"
              style={{ color: 'var(--ms-text-muted)' }}
            >
              {statusLabel && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="w-[6px] h-[6px] rounded-full"
                    style={{
                      background:
                        statusLabel.tone === 'open'
                          ? '#7a9a4e'
                          : statusLabel.tone === 'soon'
                            ? '#f59e0b'
                            : 'var(--ms-text-muted)',
                    }}
                  />
                  {statusLabel.text}
                </span>
              )}
              {statusLabel && <span aria-hidden>·</span>}
              <span>{t('pickupAvailable')}</span>
              {pickupWaitMinutes ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
                </>
              ) : null}
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onViewMenu}
                className="inline-flex items-center px-8 py-3.5 text-[15px] font-bold transition hover:brightness-105 active:scale-[0.98]"
                style={{
                  background: 'var(--ms-accent)',
                  color: '#fdf6ec',
                  borderRadius: 'calc(var(--ms-radius) * 1.2)',
                  boxShadow: '0 12px 26px -12px color-mix(in srgb, var(--ms-accent) 65%, transparent)',
                }}
              >
                {t('orderOnline')}
              </button>
              {hasDelivery && onDelivery && (
                <button
                  type="button"
                  onClick={onDelivery}
                  className="inline-flex items-center px-6 py-3.5 text-[14.5px] font-semibold transition hover:bg-black/5"
                  style={{
                    color: OLIVE,
                    border: `1.5px solid color-mix(in srgb, ${OLIVE} 55%, transparent)`,
                    borderRadius: 'calc(var(--ms-radius) * 1.2)',
                  }}
                >
                  {t('delivery')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
